import React, { useState } from "react";
import { Alert, PageHeader, Tabs } from "../../design-system";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { HoursReport } from "./HoursReport";
import { AbsencesReport } from "./AbsencesReport";
import MyReportsCard from "./MyReportsCard";

/**
 * Hub de reportes. Cada reporte vive en su propio componente
 * (HoursReport, AbsencesReport). El hub gestiona:
 *  - Gating de acceso (manager-only)
 *  - PageHeader compartido + descripción contextual del tab activo
 *  - Tabs con conmutación local
 *
 * Cuando aparezca un tercer reporte (compliance docs, solicitudes…), se
 * añade un nuevo `<TabReport />` y se enchufa en `TABS` y `TAB_DESCRIPTIONS`.
 */
const TABS = [
  { id: "hours", label: "Horas trabajadas" },
  { id: "absences", label: "Ausencias" },
];

const TAB_DESCRIPTIONS = {
  hours:
    "Resumen agregado del control horario por empleado y rango de fechas. Incluye balance vs jornada planificada y horas extra (trabajadas y aprobadas).",
  absences:
    "Días de ausencia aprobados por empleado y tipo en el rango seleccionado. Opcionalmente incluye el balance vigente de vacaciones por empleado.",
};

function Reportes() {
  document.title = "Reportes | Kronaly";
  const { can } = useCurrentMembership();
  // Cada reporte tiene su propia capability; aquí gateamos el acceso al
  // hub si ninguno está disponible. Cuando solo una capability está
  // activa, ocultamos el tab del otro.
  // Horas trabajadas: visible para managers (employees:manage) Y para
  // representantes legales (time_tracking:rlt_view) — el RLT necesita
  // acceso de solo lectura por exigencia del art. 34.9 ET.
  const canHours = can("employees:manage") || can("time_tracking:rlt_view");
  const canAbsences = can("absences:approve");
  const visibleTabs = TABS.filter((t) =>
    t.id === "hours" ? canHours : t.id === "absences" ? canAbsences : true,
  );

  const [active, setActive] = useState(visibleTabs[0]?.id || "hours");

  if (visibleTabs.length === 0) {
    return (
      <div className="kr-page">
        <PageHeader eyebrow="Reportes" title="Sin acceso" />
        <Alert tone="warning">
          Necesitas permisos de gestión para ver reportes agregados.
        </Alert>
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Reportes"
        title="Reportes agregados"
        description={TAB_DESCRIPTIONS[active]}
      />
      <Tabs tabs={visibleTabs} value={active} onChange={setActive} />
      {active === "hours" && canHours && <HoursReport isManager={canHours} />}
      {active === "absences" && canAbsences && (
        <AbsencesReport isManager={canAbsences} />
      )}
      <MyReportsCard />
    </div>
  );
}

export default Reportes;
